import React, { useState } from 'react';
import { ArrowUpRight, Star, Minus, ArrowRight, Layers, Ruler, PenTool, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import ConsultFormModal from '../components/ConsultFormModal';
import video from '../assets/interior.mp4'; 

const Services = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const serviceData = [
    {
      id: "01",
      title: "Residential Artistry",
      subtitle: "Private Sanctuaries",
      desc: "We curate homes that serve as a retreat for the soul. From Italian marble flooring to bespoke chandeliers, every inch is designed to reflect your stature.",
      img: "https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&q=80",
      tags: ["Villa Interiors", "Penthouse Design", "Smart Living"],
      frameClass: "rounded-t-[12rem] rounded-b-xl" // Arch Shape
    },
    {
      id: "02",
      title: "Commercial Prestige",
      subtitle: "Corporate Excellence",
      desc: "Redefining the corporate landscape with interiors that exude power and sophistication. Boardrooms and lobbies that leave a lasting impression on your clients.",
      img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&q=80",
      tags: ["Luxury Retail", "Executive Offices", "Hospitality"],
      frameClass: "rounded-[4rem] rounded-tr-[10rem]" // Abstract Shape
    },
    {
      id: "03",
      title: "Bespoke Furnishing",
      subtitle: "Handcrafted Luxury",
      desc: "Furniture that isn't just functional, but sculptural. We collaborate with global artisans to create one-of-a-kind pieces tailored specifically for your space.",
      img: "https://images.unsplash.com/photo-1556228453-efd6c1ff04f6?auto=format&fit=crop&q=80",
      tags: ["Custom Joinery", "Italian Leather", "Gold Leafing"],
      frameClass: "rounded-tl-[8rem] rounded-br-[8rem] rounded-tr-2xl rounded-bl-2xl" // Leaf Shape
    }
  ];

  const processSteps = [
    { icon: <Layers size={24}/>, title: "Concept", desc: "Moodboards & layout planning." },
    { icon: <PenTool size={24}/>, title: "Design", desc: "3D Visuals & material selection." },
    { icon: <Ruler size={24}/>, title: "Execution", desc: "Project management & build." },
    { icon: <CheckCircle size={24}/>, title: "Handover", desc: "Final styling & reveal." },
  ];

  return (
    <div className="bg-[#FAFAF9] text-stone-800 min-h-screen font-sans selection:bg-stone-200 selection:text-stone-900">
      <Helmet>
        <title>Services | Luxuriya Homes</title>
      </Helmet>

      {/* ================= 1. HERO SECTION (Light Theme + Video) ================= */}
      <section className="relative pt-32 pb-20 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        
        {/* Top Text Content */}
        <div className="flex flex-col md:flex-row items-end justify-between gap-12 mb-16">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-6">
               <span className="h-[1px] w-12 bg-amber-600"></span>
               <span className="text-amber-700 uppercase tracking-[0.25em] text-xs font-semibold">Our Expertise</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-serif text-stone-900 leading-[0.9]">
              Curating <br/>
              <span className="italic text-stone-400 font-light">Timeless</span> Value.
            </h1>
          </motion.div>

          <motion.div 
             initial={{ opacity: 0 }}
             animate={{ opacity: 1 }}
             transition={{ delay: 0.4, duration: 0.8 }}
             className="hidden md:block max-w-sm"
          >
            <p className="text-stone-500 text-sm leading-relaxed mb-6">
              We bring an editorial eye to interior design, blending classic architectural proportions with modern luxury living.
            </p>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="group flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-stone-900 hover:text-amber-700 transition-colors"
            >
              Start Project <ArrowRight size={16} className="group-hover:translate-x-2 transition-transform"/>
            </button>
          </motion.div>
        </div>

        {/* Hero VIDEO Container */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.2 }}
          className="w-full h-[60vh] md:h-[75vh] overflow-hidden rounded-sm relative shadow-2xl"
        >
            <video 
                autoPlay 
                loop 
                muted 
                playsInline 
                className="w-full h-full object-cover"
            >
                <source src={video} type="video/mp4" />
            </video>

           {/* INCREASED BLACK GRADIENT OVERLAY (Opacity 80%) */}
           <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/20 to-transparent pointer-events-none"></div>
        </motion.div>
      </section>

      {/* ================= 2. PROCESS STRIP (PERMANENT BORDERS) ================= */}
      <section className="py-24 bg-stone-50 border-y border-stone-200">
        
        <div className="text-center mb-16">
            <span className="text-amber-600 text-[10px] font-bold uppercase tracking-[0.4em] block mb-3">Workflow</span>
            <h3 className="text-3xl md:text-4xl font-serif text-stone-900">How We Create</h3>
        </div>

        <div className="max-w-[1600px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
                <div 
                  key={i} 
                  // Border changed to amber-200 (Permanent) and shadow-md (Permanent)
                  className="bg-white p-10 rounded-2xl shadow-md border border-amber-200 hover:border-amber-500 hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 flex flex-col items-center text-center gap-5 group cursor-default"
                >
                    <div className="p-5 rounded-full bg-stone-50 text-amber-700 border border-stone-100 group-hover:bg-amber-600 group-hover:text-white group-hover:border-amber-600 transition-all duration-500">
                        {step.icon}
                    </div>
                    <h4 className="font-serif text-xl text-stone-900 group-hover:text-amber-800 transition-colors">{step.title}</h4>
                    <p className="text-xs text-stone-500 uppercase tracking-widest leading-relaxed">{step.desc}</p>
                </div>
            ))}
        </div>
      </section>

      {/* ================= 3. SERVICES LIST (With Shapes) ================= */}
      <section className="py-24 px-6 md:px-12 lg:px-20 max-w-[1600px] mx-auto">
        <div className="flex flex-col gap-32">
          {serviceData.map((service, index) => (
            <div key={index} className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} gap-12 md:gap-24 items-center`}>
              
              {/* Image Side with Shapes */}
              <motion.div 
                whileInView={{ opacity: 1, y: 0 }}
                initial={{ opacity: 0, y: 40 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8 }}
                className="w-full md:w-1/2 p-4"
              >
                <div className={`relative group overflow-hidden shadow-2xl ${service.frameClass}`}>
                  <div className="absolute top-8 left-0 z-10 bg-stone-900 text-white px-6 py-2 text-sm font-serif italic">
                    {service.id}
                  </div>
                  <img 
                    src={service.img} 
                    alt={service.title} 
                    className="w-full h-[550px] object-cover hover:scale-105 transition-transform duration-1000 ease-out"
                  />
                  {/* Subtle Border */}
                  <div className={`absolute inset-4 border border-white/30 pointer-events-none ${service.frameClass}`}></div>
                </div>
              </motion.div>

              {/* Text Side */}
              <motion.div 
                whileInView={{ opacity: 1, x: 0 }}
                initial={{ opacity: 0, x: index % 2 === 0 ? 30 : -30 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="w-full md:w-1/2"
              >
                 <div className="mb-4 flex items-center gap-2">
                    <Star size={12} className="text-amber-600 fill-amber-600" />
                    <span className="text-amber-700 text-[10px] font-bold uppercase tracking-[0.3em]">{service.subtitle}</span>
                 </div>
                 
                 <h2 className="text-4xl md:text-5xl font-serif text-stone-900 mb-8">
                   {service.title}
                 </h2>
                 
                 <p className="text-stone-500 text-lg leading-relaxed font-light mb-10 max-w-md">
                   {service.desc}
                 </p>

                 <div className="flex flex-wrap gap-3 mb-10">
                   {service.tags.map((tag, i) => (
                     <span key={i} className="px-4 py-2 border border-stone-200 text-stone-600 text-xs tracking-wide hover:bg-stone-100 transition-colors cursor-default">
                       {tag}
                     </span>
                   ))}
                 </div>

                 <button 
                   onClick={() => setIsModalOpen(true)}
                   className="flex items-center gap-2 text-sm font-semibold border-b border-stone-900 pb-1 hover:text-amber-700 hover:border-amber-700 transition-all"
                 >
                   Explore Service <ArrowUpRight size={16} />
                 </button>
              </motion.div>

            </div>
          ))}
        </div>
      </section>

      {/* ================= 4. FOOTER CTA ================= */}
      <section className="py-32 bg-[#F5F5F0] border-t border-stone-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
             initial={{ opacity: 0, scale: 0.9 }}
             whileInView={{ opacity: 1, scale: 1 }}
             viewport={{ once: true }}
             transition={{ duration: 0.8 }}
          >
            <Minus size={40} className="text-amber-600 mx-auto mb-6" strokeWidth={1} />
            <h2 className="text-5xl md:text-7xl font-serif text-stone-900 mb-8">
              Your vision, <br/>
              <span className="italic text-stone-500">Exquisitely realized.</span>
            </h2>
            <button 
              onClick={() => setIsModalOpen(true)}
              className="bg-stone-900 text-white px-12 py-5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-amber-700 transition-colors duration-300 shadow-xl shadow-stone-200"
            >
              Book Appointment
            </button>
          </motion.div>
        </div>
      </section>

      {/* Modal */}
      <ConsultFormModal open={isModalOpen} setOpen={setIsModalOpen} />
    </div>
  );
};

export default Services;